import Content from './content';
import CSS from './Username.module.css';
import { useState, useRef } from 'react';
import Button from '@CS/components/Button';
import { useTranslation } from '@CS/hooks';
import { isAndroid } from '@CS/utils/Device';
import { Popup } from '@CS/components/Popups';
import type { ChangeEvent, FocusEvent } from 'react';
import type { PopupProps } from '@CS/components/Popups';
import Input from '@CS/components/Form/InputField/InputField.module.css';

type UsernameProps = PopupProps & {
  onSubmit: (username: string) => Promise<unknown>;
};

const USERNAME_PATTERN = /^(?=.{3,20}$)[\p{L}0-9_]+$/u;

export const Username = ({ onSubmit, onClose, ...props }: UsernameProps) => {
  const buttonId = 'submit';

  const {
    title,
    submit,
    description,
    placeholder,
    error: usernameError
  } = useTranslation(Content);

  const [error, setError] = useState(false);
  const [visible, setVisible] = useState(true);
  const [username, setUsername] = useState('');
  const dialog = useRef<HTMLDialogElement>(null);

  const onInput = (event: ChangeEvent<HTMLInputElement>) =>
    setUsername(event.target.value);

  const onBlur = (event: FocusEvent) =>
    event.relatedTarget?.id === buttonId && onClick();

  const onClick = () =>
    visible && void onSubmit(username)
      .then(setVisible.bind(null, false))
      .catch(setError.bind(null, true));

  return (
    <Popup
      className={(isAndroid && CSS.popupContainer) || ''}
      onClose={onClose}
      close={!visible}
      ref={dialog}
      {...props}
      hideClose
    >
      <div className={CSS.usernameContainer}>
        <h3 className={CSS.usernameTitle}>{title}</h3>
        <p className={CSS.usernameDescription}>{description}</p>

        <input
          className={`${Input.inputField} ${CSS.usernameInput}`}
          placeholder={placeholder}
          autoComplete="off"
          onChange={onInput}
          onBlur={onBlur}
          name="username"
          type="text"
        />

        {error && (
          <em className={`${Input.fieldError} ${CSS.usernameError}`}>
            {usernameError}
          </em>
        )}

        <Button
          disabled={!username.match(USERNAME_PATTERN)}
          onClick={onClick}
          text={submit}
          id={buttonId}
        />
      </div>
    </Popup>
  );
};
